import { ArrowLeftCircleIcon, ArrowRightCircleIcon } from "@heroicons/react/24/solid";
import { XCircleIcon } from "@heroicons/react/24/outline";
import { Modal } from "../../components/daisy";
import { ImageData, useImageRegistry } from "../../hooks/useImageRegistry";
import useKeyboard from "../../hooks/useKeyboard";

export interface ImageProps {
	className?: string;
	style?: React.CSSProperties;
	image: string;
	alt: string;
	onImageClick?: (e: React.MouseEvent, image: string) => void;
}

export const RenderCardImage = ({
	className,
	style,
	image,
	alt,
	onImageClick,
}: ImageProps) => {

	const handleImageClick = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		onImageClick && onImageClick(e, image);
	}

	return (
		<div className='relative'>
			<div
				className={`card overflow-hidden max-w-lg w-full mx-auto cursor-pointer hover:opacity-90 transition-opacity ${className ?? ""}`}
				style={style}
				onClick={handleImageClick}
			>
				<figure>
					<img src={image} alt={alt} className="h-auto w-full" />
				</figure>
			</div>
		</div>
	);
};

export const RenderModalImage = ({ images, initialImage, closeModal }:
	{ images: ImageData[], initialImage: string, closeModal: () => void }) => {
	const {
		hasNext,
		hasPrev,
		next,
		prev,
		currentImg
	} = useImageRegistry({ images, initialImage });

	useKeyboard({ keyCodes: ['ArrowLeft'], onKeydown: prev })
	useKeyboard({ keyCodes: ['ArrowRight'], onKeydown: next })

	return (currentImg && <Modal.Body className=''>
		<div className="relative w-full h-full flex items-center justify-center p-4 overflow-auto">
			{/* Navigation Buttons */}
			{hasPrev && (
				<button
					className="absolute left-4 sm:static sm:mr-8 z-10 p-2 text-white hover:text-gray-300 transition-colors"
					onClick={(e) => {
						e.stopPropagation();
						prev();
					}}
					aria-label="Previous image"
				>
					<ArrowLeftCircleIcon className="w-7 h-7 " />
				</button>
			)}

			{/* Close Button */}
			<button
				className="absolute top-4 right-4 z-10 p-2 text-white hover:text-gray-300 transition-colors"
				onClick={(e) => {
					e.stopPropagation();
					closeModal();
				}}
				aria-label="Close"
			>
				<XCircleIcon className="w-7 h-7" />
			</button>

			{/* Image */}
			<div className="relative group">
				<div className="w-full h-full" onClick={(e) => e.stopPropagation()}>
					<img
						src={currentImg.src}
						alt={currentImg.alt}
						className="max-h-[80vh] max-w-[90vw] object-contain"
						onClick={(e) => e.stopPropagation()}
					/>
				</div>
			</div>

			{hasNext && (
				<button
					className="absolute right-4 sm:static sm:ml-8 z-10 p-2 text-white hover:text-gray-300 transition-colors"
					onClick={(e) => {
						e.stopPropagation();
						next();
					}}
					aria-label="Next image"
				>
					<ArrowRightCircleIcon className="w-7 h-7 " />
				</button>
			)}
		</div>
	</Modal.Body>
	)
}

interface CardInfoProps {
	className?: string;
	style?: React.CSSProperties;
	title: string;
	description: React.ReactNode;
	icon: React.ReactNode;
}

export const renderCardInfo = ({ className, style, title, description, icon }: CardInfoProps) => {
	return (
		<div
			className={`card bg-base-100 shadow-xl max-w-md mx-auto w-full ${className ?? ""}`}
			style={style}
		>
			<div className="card-body">
				<h3 className="card-title text-xl font-bold flex items-center">
					{icon}
					{title}
				</h3>
				<ul className="list-disc list-none">
					{description}
				</ul>
			</div>
		</div>
	);
};


